import { Button, Empty, Loading } from "@components";
import styles from "@styles/containers/cdp/customer-coupons.module.scss";
import classNames from "classnames/bind";
import { useEffect, useState } from "react";
import { If } from "shared/components/control-statements";
import { InputSearch } from "shared/components/InputSearch";
import Coupon from "./Coupon";
import { useDebouncedCallback, useIsInViewport } from "@hooks";
import { useRef } from "react";
import InternalBenefitAPI from "providers/InternalBenefitAPI";
import { AddCoupon } from "./AddCoupon";
const css = classNames.bind(styles);

const filters = [
  {
    id: "ALL",
    name: "Tudo",
  },
  {
    id: "PENDING",
    name: "Disponíveis",
  },
  {
    id: "USED",
    name: "Utilizados",
  },
  {
    id: "EXPIRED",
    name: "Expirados",
  },
  {
    id: "DELETED",
    name: "Removidos",
  },
];

export const CustomerCoupons = ({ customer }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [activeFilter, setActiveFilter] = useState(filters[0]);
  const [search, setSearch] = useState("");
  const [openAddCoupon, setOpenAddCoupon] = useState(false);

  const [coupons, setCoupons] = useState([]);

  const [loading, setLoading] = useState(false);
  const [hasNext, setHasNext] = useState(true);
  const [length, setLength] = useState(0);
  const [page, setPage] = useState(0);
  const loadingRef = useRef(null);
  const isVisible = useIsInViewport(loadingRef);

  useEffect(() => {
    if (hasNext && isVisible) {
      applyFilters({
        page,
        term: search,
        status: activeFilter,
      });
    }
  }, [isVisible]);

  const loadHistory = async ({ term, status, page, refresh = false }) => {
    setLoading(true);

    const limitByPage = 20;

    const internalBenefitAPI = new InternalBenefitAPI();

    const historyResponse = await internalBenefitAPI.getCouponHistory({
      customerId: customer.id,
      term,
      status,
      page,
      limit: limitByPage,
    });

    const couponPage = historyResponse.getData({
      total: 0,
      items: [],
      next: { page: -1, limit: limitByPage },
    });

    let newCoupons = [];

    if (refresh) {
      newCoupons = [...couponPage.items];
    } else {
      newCoupons = [...coupons, ...couponPage.items];
    }

    setCoupons(newCoupons);
    setLength(couponPage.total);
    setHasNext(couponPage.next.page != -1);
    setPage(couponPage.next.page != -1 ? page + 1 : page);
    setLoading(false);
  };

  const applyFilters = async ({ page, term, status, refresh }) => {
    await loadHistory({
      page,
      term,
      status: status.id === "ALL" ? null : status.id,
      refresh,
    });
  }

  const debouncedSearch = useDebouncedCallback((nextValue) => {
    applyFilters({
      page: 0,
      term: nextValue,
      status: activeFilter,
      refresh: true,
    });
  }, 1000);

  const handleChangeFilter = (filter) => {
    setActiveFilter(filter);
    applyFilters({
      page: 0,
      term: search,
      status: filter,
      refresh: true,
    });
  };

  const removeCoupon = async (coupon) => {
    
    const internalBenefitAPI = new InternalBenefitAPI();

    const response = await internalBenefitAPI.removeCoupon({
      customerId: customer.id,
      customerCouponId: coupon.id
    });

    if (response.getErrors([]).length > 0) {
      throw new Error(response.getErrors([])[0]);
    }

    setActiveFilter(filters[0])

    applyFilters({
      page: 0,
      term: search,
      status: filters[0],
      refresh: true,
    });
  };

  return (
    <div className={styles.couponsContainer}>

      <div className="d-flex align-center justify-between">
        <p className={styles.title}>
          Benefícios ({length})
        </p>
        <div className={styles.filters}>
          <div
            className={css({
              selectedFilter: true,
              collapsed: collapsed,
            })}
            onClick={() => setCollapsed(!collapsed)}
          >
            <p>{activeFilter?.name}</p>
            <i className="fas fa-chevron-down" />
          </div>

          <div
            className={css({
              collapsedCategories: true,
              in: collapsed,
              out: !collapsed,
            })}
          >
            <ul className={styles.options}>
              {(filters || [])
                .filter((m) => m.id !== activeFilter?.id)
                .map((m) => (
                  <li
                    key={m.name}
                    onClick={() => {
                      handleChangeFilter(m);
                      setCollapsed(false);
                    }}
                  >
                    {m.name}
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </div>

      <InputSearch
        design="two"
        placeholder="Buscar por..."
        className="mt-15 mb-15"
        onChange={(e) => {
          setSearch(e.target.value);
          debouncedSearch(e.target.value);
        }}
        occurrences={coupons.length}
      />

      <div className={styles.coupons}>
        <If cond={coupons.length > 0}>
          {coupons.map((coupon, index) => (
            <Coupon 
              key={index} 
              coupon={coupon} 
              onRemove={() => removeCoupon(coupon)}
            />
          ))}
        </If>

        <If cond={coupons.length === 0 && !hasNext && !loading}>
          <Empty>
            <p>Nenhum cupom encontrado</p>
          </Empty>
        </If>

        <If cond={loading}>
          <div className={styles.loading}>
            <div className="w-100 d-flex align-center justify-center flex-1 pt-25 pb-25">
              <Loading />
            </div>
          </div>
        </If>

        <div ref={loadingRef}>&nbsp;</div>
      </div>

      <div className={styles.footer}>
        <Button
          design="underline"
          className="mb-10"
          onClick={() => setOpenAddCoupon(true)}
        >
          Adicionar benefício
        </Button>
      </div>

      <AddCoupon
        visible={openAddCoupon}
        customer={customer}
        onClose={() => setOpenAddCoupon(false)}
        onAdd={() => {
          setOpenAddCoupon(false);

          setActiveFilter(filters[0])

          applyFilters({
            page: 0,
            term: search,
            status: filters[0],
            refresh: true,
          });
        }}
      />

    </div>
  );
};
